import React, { useState, useEffect } from 'react';
import { Calendar, MapPin, DollarSign, Clock, User, CheckCircle, XCircle, AlertCircle, Send, MessageSquare, Search, Download, Mail, Plane, Building, FileText, Eye, ChevronRight, ChevronDown, Plus } from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';


const CorporateTravelRequestApprovalWorkflow = () => {
  const [activeView, setActiveView] = useState('dashboard');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showNewRequest, setShowNewRequest] = useState(false);
  const [expandedRequest, setExpandedRequest] = useState(null);

  // Mock data for travel requests
  const [travelRequests, setTravelRequests] = useState([
    {
      id: 'TR-2024-001',
      requestor: {
        name: 'John Smith',
        email: 'john.smith@acme.com',
        department: 'Sales',
        employeeId: 'EMP001'
      },
      tripDetails: {
        purpose: 'Client Meeting',
        destination: 'New York, NY',
        departureDate: '2024-02-15',
        returnDate: '2024-02-18',
        businessJustification: 'Quarterly business review with key client - potential $2M deal'
      },
      budget: {
        estimated: 3200,
        approved: 3000,
        currency: 'USD',
        breakdown: {
          flights: 1200,
          hotel: 1200,
          meals: 400,
          transport: 200
        }
      },
      status: 'pending',
      currentApprover: 'manager',
      priority: 'high',
      submittedAt: '2024-01-15T10:30:00Z',
      approvalWorkflow: [
        { step: 'manager', approver: 'Sarah Johnson', status: 'pending', timestamp: null },
        { step: 'finance', approver: 'Mike Chen', status: 'waiting', timestamp: null },
        { step: 'travel_ops', approver: 'Lisa Wang', status: 'waiting', timestamp: null }
      ],
      compliance: {
        policyViolations: [],
        riskLevel: 'low',
        approvedBudget: true
      },
      attachments: ['travel_request.pdf', 'client_invitation.pdf']
    },
    {
      id: 'TR-2024-002',
      requestor: {
        name: 'Emma Davis',
        email: 'emma.davis@acme.com',
        department: 'Marketing',
        employeeId: 'EMP002'
      },
      tripDetails: {
        purpose: 'Conference',
        destination: 'London, UK',
        departureDate: '2024-02-20',
        returnDate: '2024-02-25',
        businessJustification: 'Marketing Summit 2024 - industry networking and partnerships'
      },
      budget: {
        estimated: 4500,
        approved: null,
        currency: 'USD',
        breakdown: {
          flights: 2000,
          hotel: 1800,
          meals: 500,
          transport: 200
        }
      },
      status: 'approved',
      currentApprover: null,
      priority: 'medium',
      submittedAt: '2024-01-12T14:20:00Z',
      approvalWorkflow: [
        { step: 'manager', approver: 'David Wilson', status: 'approved', timestamp: '2024-01-13T09:15:00Z' },
        { step: 'finance', approver: 'Mike Chen', status: 'approved', timestamp: '2024-01-13T11:30:00Z' },
        { step: 'travel_ops', approver: 'Lisa Wang', status: 'approved', timestamp: '2024-01-13T15:45:00Z' }
      ],
      compliance: {
        policyViolations: ['hotel_rate_exceeded'],
        riskLevel: 'medium',
        approvedBudget: true
      },
      attachments: ['conference_agenda.pdf']
    },
    {
      id: 'TR-2024-003',
      requestor: {
        name: 'Robert Chen',
        email: 'robert.chen@acme.com',
        department: 'Engineering',
        employeeId: 'EMP003'
      },
      tripDetails: {
        purpose: 'Training',
        destination: 'San Francisco, CA',
        departureDate: '2024-03-01',
        returnDate: '2024-03-05',
        businessJustification: 'Technical certification training for cloud architecture'
      },
      budget: {
        estimated: 2800,
        approved: null,
        currency: 'USD',
        breakdown: {
          flights: 800,
          hotel: 1200,
          meals: 400,
          transport: 150,
          training: 250
        }
      },
      status: 'rejected',
      currentApprover: null,
      priority: 'low',
      submittedAt: '2024-01-10T16:45:00Z',
      approvalWorkflow: [
        { step: 'manager', approver: 'Sarah Johnson', status: 'rejected', timestamp: '2024-01-11T10:20:00Z', comment: 'Training can be completed online. Budget needs to be reallocated.' }
      ],
      compliance: {
        policyViolations: ['training_alternative_available'],
        riskLevel: 'low',
        approvedBudget: false
      },
      attachments: []
    }
  ]);

  const filteredRequests = travelRequests?.filter(request => {
    const matchesSearch = request?.requestor?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         request?.tripDetails?.destination?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         request?.id?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesFilter = filterStatus === 'all' || request?.status === filterStatus;
    return matchesSearch && matchesFilter;
  }) || [];

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-600';
      case 'medium': return 'text-yellow-600';
      case 'low': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  const RequestCard = ({ request }) => (
    <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start space-x-4">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
            <User className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <h3 className="font-semibold text-gray-900">{request?.requestor?.name}</h3>
              <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(request?.status)}`}>
                {request?.status}
              </span>
              <span className={`text-xs font-medium ${getPriorityColor(request?.priority)}`}>
                {request?.priority} priority
              </span>
            </div>
            <p className="text-sm text-gray-600">{request?.requestor?.department} • {request?.id}</p>
          </div>
        </div>
        <div className="flex space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedRequest(request)}
          >
            <Eye className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setExpandedRequest(expandedRequest === request?.id ? null : request?.id)}
          >
            {expandedRequest === request?.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="flex items-center space-x-2">
          <MapPin className="w-4 h-4 text-gray-400" />
          <span className="text-sm text-gray-600">{request?.tripDetails?.destination}</span>
        </div>
        <div className="flex items-center space-x-2">
          <Calendar className="w-4 h-4 text-gray-400" />
          <span className="text-sm text-gray-600">
            {new Date(request?.tripDetails?.departureDate)?.toLocaleDateString()} - {new Date(request?.tripDetails?.returnDate)?.toLocaleDateString()}
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <DollarSign className="w-4 h-4 text-gray-400" />
          <span className="text-sm text-gray-600">${request?.budget?.estimated?.toLocaleString()}</span>
        </div>
      </div>

      <div className="mb-4">
        <p className="text-sm font-medium text-gray-700 mb-1">Trip Purpose:</p>
        <p className="text-sm text-gray-600">{request?.tripDetails?.purpose}</p>
      </div>

      {/* Approval Workflow */}
      <div className="mb-4">
        <p className="text-sm font-medium text-gray-700 mb-2">Approval Progress:</p>
        <div className="flex items-center space-x-2">
          {request?.approvalWorkflow?.map((step, index) => (
            <React.Fragment key={step?.step}>
              <div className="flex items-center space-x-1">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                  step?.status === 'approved' ? 'bg-green-500 text-white' :
                  step?.status === 'rejected' ? 'bg-red-500 text-white' :
                  step?.status === 'pending'? 'bg-blue-500 text-white' : 'bg-gray-300 text-gray-600'
                }`}>
                  {step?.status === 'approved' ? '✓' :
                   step?.status === 'rejected' ? '✗' :
                   step?.status === 'pending' ? '!' : index + 1}
                </div>
                <span className="text-xs text-gray-600 capitalize">{step?.step?.replace('_', ' ')}</span>
              </div>
              {index < request?.approvalWorkflow?.length - 1 && (
                <ChevronRight className="w-3 h-3 text-gray-400" />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {expandedRequest === request?.id && (
        <div className="border-t border-gray-200 pt-4 space-y-4">
          <div>
            <p className="text-sm font-medium text-gray-700 mb-1">Business Justification:</p>
            <p className="text-sm text-gray-600">{request?.tripDetails?.businessJustification}</p>
          </div>
          
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Budget Breakdown:</p>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {Object.entries(request?.budget?.breakdown || {})?.map(([key, value]) => (
                <div key={key} className="flex justify-between">
                  <span className="text-gray-600 capitalize">{key}:</span>
                  <span className="font-medium">${value}</span>
                </div>
              ))}
            </div>
          </div>

          {request?.compliance?.policyViolations?.length > 0 && (
            <div>
              <p className="text-sm font-medium text-red-700 mb-1">Policy Violations:</p>
              <ul className="text-sm text-red-600 space-y-1">
                {request?.compliance?.policyViolations?.map((violation, index) => (
                  <li key={index} className="flex items-center space-x-1">
                    <AlertCircle className="w-3 h-3" />
                    <span className="capitalize">{violation?.replace('_', ' ')}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex justify-end space-x-2">
            {request?.status === 'pending' && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => console.log('Reject request:', request?.id)}
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  Reject
                </Button>
                <Button
                  size="sm"
                  onClick={() => console.log('Approve request:', request?.id)}
                >
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Approve
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const RequestDetailModal = () => {
    if (!selectedRequest) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Travel Request Details - {selectedRequest?.id}
                </h2>
                <p className="text-sm text-gray-600">
                  Submitted by {selectedRequest?.requestor?.name} on {new Date(selectedRequest?.submittedAt)?.toLocaleDateString()}
                </p>
              </div>
              <Button
                variant="ghost"
                onClick={() => setSelectedRequest(null)}
              >
                ×
              </Button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Requestor Information */}
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Requestor Information</h3>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <User className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium">{selectedRequest?.requestor?.name}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600">{selectedRequest?.requestor?.email}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Building className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600">{selectedRequest?.requestor?.department}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Trip Details</h3>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600">{selectedRequest?.tripDetails?.destination}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600">
                      {new Date(selectedRequest?.tripDetails?.departureDate)?.toLocaleDateString()} - {new Date(selectedRequest?.tripDetails?.returnDate)?.toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <FileText className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600">{selectedRequest?.tripDetails?.purpose}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Business Justification */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Business Justification</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-700">{selectedRequest?.tripDetails?.businessJustification}</p>
              </div>
            </div>

            {/* Budget Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Budget Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-blue-900">Estimated Budget</p>
                  <p className="text-2xl font-bold text-blue-600">${selectedRequest?.budget?.estimated?.toLocaleString()}</p>
                </div>
                {selectedRequest?.budget?.approved && (
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-sm font-medium text-green-900">Approved Budget</p>
                    <p className="text-2xl font-bold text-green-600">${selectedRequest?.budget?.approved?.toLocaleString()}</p>
                  </div>
                )}
              </div>
              <div className="mt-4 space-y-2">
                <p className="text-sm font-medium text-gray-700">Budget Breakdown:</p>
                {Object.entries(selectedRequest?.budget?.breakdown || {})?.map(([key, value]) => (
                  <div key={key} className="flex justify-between py-1">
                    <span className="text-sm text-gray-600 capitalize">{key?.replace('_', ' ')}</span>
                    <span className="text-sm font-medium">${value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Approval Timeline */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Approval Timeline</h3>
              <div className="space-y-4">
                {selectedRequest?.approvalWorkflow?.map((step, index) => (
                  <div key={step?.step} className="flex items-start space-x-4">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium mt-1 ${
                      step?.status === 'approved' ? 'bg-green-500 text-white' :
                      step?.status === 'rejected' ? 'bg-red-500 text-white' :
                      step?.status === 'pending'? 'bg-blue-500 text-white' : 'bg-gray-300 text-gray-600'
                    }`}>
                      {step?.status === 'approved' ? '✓' :
                       step?.status === 'rejected' ? '✗' :
                       step?.status === 'pending' ? '!' : index + 1}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900 capitalize">{step?.step?.replace('_', ' ')} Approval</p>
                          <p className="text-sm text-gray-600">{step?.approver}</p>
                        </div>
                        <div className="text-right">
                          <p className={`text-sm font-medium ${
                            step?.status === 'approved' ? 'text-green-600' :
                            step?.status === 'rejected' ? 'text-red-600' :
                            step?.status === 'pending'? 'text-blue-600' : 'text-gray-500'
                          }`}>
                            {step?.status}
                          </p>
                          {step?.timestamp && (
                            <p className="text-xs text-gray-500">
                              {new Date(step?.timestamp)?.toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>
                      {step?.comment && (
                        <div className="mt-2 bg-yellow-50 p-2 rounded text-sm text-yellow-800">
                          {step?.comment}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Compliance Information */}
            {(selectedRequest?.compliance?.policyViolations?.length > 0 || selectedRequest?.compliance?.riskLevel !== 'low') && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Compliance & Risk</h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-700">Risk Level:</span>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      selectedRequest?.compliance?.riskLevel === 'high' ? 'bg-red-100 text-red-800' :
                      selectedRequest?.compliance?.riskLevel === 'medium'? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                    }`}>
                      {selectedRequest?.compliance?.riskLevel}
                    </span>
                  </div>
                  {selectedRequest?.compliance?.policyViolations?.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-red-700 mb-2">Policy Violations:</p>
                      <ul className="space-y-1">
                        {selectedRequest?.compliance?.policyViolations?.map((violation, index) => (
                          <li key={index} className="flex items-center space-x-2 text-sm text-red-600">
                            <AlertCircle className="w-4 h-4" />
                            <span className="capitalize">{violation?.replace('_', ' ')}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              {selectedRequest?.status === 'pending' && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => {
                      console.log('Request more information for:', selectedRequest?.id);
                      setSelectedRequest(null);
                    }}
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Request Info
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      console.log('Reject request:', selectedRequest?.id);
                      setSelectedRequest(null);
                    }}
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Reject
                  </Button>
                  <Button
                    onClick={() => {
                      console.log('Approve request:', selectedRequest?.id);
                      setSelectedRequest(null);
                    }}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Approve
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const NewRequestModal = () => {
    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState({
      tripPurpose: '',
      destination: '',
      departureDate: '',
      returnDate: '',
      businessJustification: '',
      estimatedBudget: '',
      urgency: 'medium'
    });

    const steps = [
      { number: 1, title: 'Trip Details', icon: Plane },
      { number: 2, title: 'Business Case', icon: FileText },
      { number: 3, title: 'Budget & Review', icon: DollarSign }
    ];

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">New Travel Request</h2>
              <Button variant="ghost" onClick={() => setShowNewRequest(false)}>×</Button>
            </div>
            
            {/* Step indicator */}
            <div className="flex items-center space-x-4 mt-4">
              {steps?.map((step, index) => (
                <div key={step?.number} className="flex items-center">
                  <div className={`flex items-center space-x-2 px-3 py-1 rounded-full ${
                    currentStep >= step?.number ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'
                  }`}>
                    <step.icon className="w-4 h-4" />
                    <span className="text-sm font-medium">{step?.title}</span>
                  </div>
                  {index < steps?.length - 1 && <ChevronRight className="w-4 h-4 text-gray-400 mx-2" />}
                </div>
              ))}
            </div>
          </div>

          <div className="p-6">
            {currentStep === 1 && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Trip Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Trip Purpose</label>
                    <Select
                      value={formData?.tripPurpose}
                      onChange={(value) => setFormData({...formData, tripPurpose: value})}
                      options={[
                        { value: 'client_meeting', label: 'Client Meeting' },
                        { value: 'conference', label: 'Conference' },
                        { value: 'training', label: 'Training' },
                        { value: 'site_visit', label: 'Site Visit' },
                        { value: 'other', label: 'Other' }
                      ]}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Destination</label>
                    <Input
                      placeholder="City, Country"
                      value={formData?.destination}
                      onChange={(e) => setFormData({...formData, destination: e?.target?.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Departure Date</label>
                    <Input
                      type="date"
                      value={formData?.departureDate}
                      onChange={(e) => setFormData({...formData, departureDate: e?.target?.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Return Date</label>
                    <Input
                      type="date"
                      value={formData?.returnDate}
                      onChange={(e) => setFormData({...formData, returnDate: e?.target?.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Priority</label>
                    <Select
                      value={formData?.urgency}
                      onChange={(value) => setFormData({...formData, urgency: value})}
                      options={[
                        { value: 'low', label: 'Low' },
                        { value: 'medium', label: 'Medium' },
                        { value: 'high', label: 'High' },
                        { value: 'urgent', label: 'Urgent' }
                      ]}
                    />
                  </div>
                </div>
                <Button onClick={() => setCurrentStep(2)} className="w-full mt-6">
                  Continue to Business Case
                </Button>
              </div>
            )}

            {currentStep === 2 && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Business Justification</h3>
                <div>
                  <label className="block text-sm font-medium mb-2">Business Justification</label>
                  <textarea
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows="6"
                    placeholder="Please provide detailed business justification for this trip including expected outcomes, client/business benefits, and why this trip cannot be conducted virtually..."
                    value={formData?.businessJustification}
                    onChange={(e) => setFormData({...formData, businessJustification: e?.target?.value})}
                  />
                </div>
                <div className="flex space-x-3">
                  <Button variant="outline" onClick={() => setCurrentStep(1)}>
                    Back
                  </Button>
                  <Button onClick={() => setCurrentStep(3)} className="flex-1">
                    Continue to Budget
                  </Button>
                </div>
              </div>
            )}

            {currentStep === 3 && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">Budget Estimation</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Estimated Total Budget</label>
                    <Input
                      type="number"
                      placeholder="3000"
                      value={formData?.estimatedBudget}
                      onChange={(e) => setFormData({...formData, estimatedBudget: e?.target?.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Currency</label>
                    <Select
                      options={[
                        { value: 'USD', label: 'USD' },
                        { value: 'EUR', label: 'EUR' },
                        { value: 'GBP', label: 'GBP' }
                      ]}
                    />
                  </div>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-3">Request Summary</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Destination:</span>
                      <span className="font-medium">{formData?.destination}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Dates:</span>
                      <span className="font-medium">{formData?.departureDate} - {formData?.returnDate}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Purpose:</span>
                      <span className="font-medium capitalize">{formData?.tripPurpose?.replace('_', ' ')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Budget:</span>
                      <span className="font-medium">${formData?.estimatedBudget}</span>
                    </div>
                  </div>
                </div>

                <div className="flex space-x-3">
                  <Button variant="outline" onClick={() => setCurrentStep(2)}>
                    Back
                  </Button>
                  <Button
                    onClick={() => {
                      console.log('Submit travel request:', formData);
                      setShowNewRequest(false);
                    }}
                    className="flex-1"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Submit Request
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <h1 className="text-2xl font-bold text-gray-900">Travel Requests</h1>
              <div className="flex space-x-1">
                <Button
                  variant={activeView === 'dashboard' ? 'default' : 'ghost'}
                  onClick={() => setActiveView('dashboard')}
                >
                  Dashboard
                </Button>
                <Button
                  variant={activeView === 'approvals' ? 'default' : 'ghost'}
                  onClick={() => setActiveView('approvals')}
                >
                  My Approvals
                </Button>
                <Button
                  variant={activeView === 'analytics' ? 'default' : 'ghost'}
                  onClick={() => setActiveView('analytics')}
                >
                  Analytics
                </Button>
              </div>
            </div>
            <Button onClick={() => setShowNewRequest(true)}>
              <Plus className="w-4 h-4 mr-2" />
              New Request
            </Button>
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-6 mb-6">
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Requests</p>
                <p className="text-3xl font-bold text-gray-900">{travelRequests?.length || 0}</p>
              </div>
              <FileText className="w-8 h-8 text-blue-600" />
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pending Approval</p>
                <p className="text-3xl font-bold text-yellow-600">
                  {travelRequests?.filter(r => r?.status === 'pending')?.length || 0}
                </p>
              </div>
              <Clock className="w-8 h-8 text-yellow-600" />
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Approved</p>
                <p className="text-3xl font-bold text-green-600">
                  {travelRequests?.filter(r => r?.status === 'approved')?.length || 0}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Budget</p>
                <p className="text-3xl font-bold text-blue-600">
                  ${travelRequests?.reduce((sum, r) => sum + (r?.budget?.estimated || 0), 0)?.toLocaleString() || 0}
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-blue-600" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                <Input
                  placeholder="Search requests..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                  className="pl-10 w-64"
                />
              </div>
              <Select
                value={filterStatus}
                onChange={(value) => setFilterStatus(value)}
                options={[
                  { value: 'all', label: 'All Status' },
                  { value: 'pending', label: 'Pending' },
                  { value: 'approved', label: 'Approved' },
                  { value: 'rejected', label: 'Rejected' }
                ]}
              />
            </div>
            <Button variant="outline" size="sm">
              <Download className="w-4 h-4 mr-1" />
              Export
            </Button>
          </div>
        </div>

        {/* Request Cards */}
        <div className="space-y-4">
          {filteredRequests?.map(request => (
            <RequestCard key={request?.id} request={request} />
          ))}
        </div>
      </div>
      {/* Modals */}
      {showNewRequest && <NewRequestModal />}
      <RequestDetailModal />
    </div>
  );
};

export default CorporateTravelRequestApprovalWorkflow;